import { serializeError } from "serialize-error";
import { createApi } from "@reduxjs/toolkit/query/react";
import { supabase, supabaseBaseQuery, type Database } from "../../../supabase";
import type { NewFolder } from "../../../types/Resource";
import { chatsApi } from "../../chats/api/chatsApi";

const resourcesApi = createApi({
  reducerPath: "api.Resources",
  baseQuery: supabaseBaseQuery,
  tagTypes: ["Resources", "ResourcePermissions", "DeletedResources"],
  endpoints: (build) => ({
    getResources: build.query<Database["public"]["Views"]["user_resources"]["Row"][], undefined>({
      queryFn: async () => {
        const { data, error } = await supabase.from("user_resources").select("*");

        if (error) return { error: serializeError(error) };

        return { data };
      },
      providesTags: [{ type: "Resources" }],
    }),

    deleteResource: build.mutation<
      Database["public"]["Functions"]["delete_resource"]["Returns"],
      { resourceId: string }
    >({
      queryFn: async ({ resourceId }) => {
        const { data, error } = await supabase.rpc("delete_resource", {
          p_resource_id: resourceId,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async ({ resourceId }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const cachedResources = resourcesApi.endpoints.getResources.select(undefined)(state);
        const resource = cachedResources.data?.find((r) => r.id === resourceId);
        const resourceType = resource?.type;

        try {
          await queryFulfilled;

          if (resourceType === "chat") {
            dispatch(chatsApi.util.invalidateTags([{ type: "Chat", id: resourceId }]));
          }
        } catch {
          // Ignore errors
        }
      },
      invalidatesTags: [{ type: "Resources" }, { type: "DeletedResources" }],
    }),

    hardDeleteResource: build.mutation<
      Database["public"]["Functions"]["hard_delete_resource"]["Returns"],
      { resourceId: string }
    >({
      queryFn: async ({ resourceId }) => {
        const { data, error } = await supabase.rpc("hard_delete_resource", {
          p_resource_id: resourceId,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      invalidatesTags: [{ type: "DeletedResources" }],
    }),

    restoreResources: build.mutation<
      Database["public"]["Functions"]["restore_resources"]["Returns"],
      {
        id: string;
        parent_id?: string | null;
      }[]
    >({
      queryFn: async (resourceToRestore) => {
        const { data, error } = await supabase.rpc("restore_resources", {
          p_resources: resourceToRestore,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      invalidatesTags: [{ type: "Resources" }, { type: "DeletedResources" }],
    }),

    getDeletedResources: build.query<
      Database["public"]["Functions"]["get_deleted_resources"]["Returns"],
      undefined
    >({
      queryFn: async () => {
        const { data, error } = await supabase.rpc("get_deleted_resources");

        if (error) return { error: serializeError(error) };

        return { data };
      },
      providesTags: [{ type: "DeletedResources" }],
    }),

    moveResource: build.mutation<
      Database["public"]["Functions"]["move_resource"]["Returns"],
      {
        resourceId: string;
        targetFolderId: string | null;
      }
    >({
      queryFn: async ({ resourceId, targetFolderId }) => {
        const { data, error } = await supabase.rpc("move_resource", {
          p_resource_id: resourceId,
          p_target_folder_id: targetFolderId || undefined,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },
      onQueryStarted: async ({ resourceId, targetFolderId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          resourcesApi.util.updateQueryData("getResources", undefined, (draft) => {
            // @ts-ignore - sometimes triggers TS2589 error: Type instantiation is excessively deep and possibly infinite.
            const resource = draft.find((res) => res.id === resourceId);

            if (resource) {
              resource.parent_id = targetFolderId;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: "Resources" }],
    }),

    renameResource: build.mutation<
      boolean,
      {
        resourceId: string;
        name: string;
      }
    >({
      queryFn: async ({ resourceId, name }) => {
        const { data: hasRenamed, error } = await supabase.rpc("rename_resource", {
          p_resource_id: resourceId,
          p_name: name,
        });

        if (error) return { error: serializeError(error) };

        return { data: hasRenamed };
      },

      onQueryStarted: async ({ resourceId, name }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          resourcesApi.util.updateQueryData("getResources", undefined, (draft) => {
            const resource = draft.find((res) => res.id === resourceId);

            if (resource) {
              resource.name = name;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },

      invalidatesTags: (result, error, { resourceId }) => [
        {
          type: "Resources",
        },
        {
          type: "Resources",
          id: resourceId,
        },
      ],
    }),

    getResource: build.query<
      Database["public"]["Views"]["user_resources"]["Row"],
      { resourceId: string }
    >({
      queryFn: async ({ resourceId }) => {
        const { data, error } = await supabase
          .from("user_resources")
          .select("*")
          .eq("id", resourceId)
          .single();

        if (error) return { error: serializeError(error) };

        return { data };
      },
      providesTags: (_, __, { resourceId }) => [{ type: "Resources", id: resourceId }],
    }),

    getResourcePermissions: build.query<
      Database["public"]["Functions"]["get_resource_permissions"]["Returns"],
      { resourceId: string }
    >({
      queryFn: async ({ resourceId }) => {
        const { data, error } = await supabase.rpc("get_resource_permissions", {
          p_resource_id: resourceId,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },

      providesTags: (_, __, { resourceId }) => [{ type: "ResourcePermissions", id: resourceId }],
    }),

    shareResource: build.mutation<
      Database["public"]["Functions"]["share_resource"]["Returns"],
      {
        resourceId: string;
        access: "viewer" | "editor";
        emails: string[];
      }
    >({
      queryFn: async ({ resourceId, emails, access }) => {
        const { data, error } = await supabase.rpc("share_resource", {
          p_resource_id: resourceId,
          p_shared_with: emails,
          p_role: access,
        });

        if (error) return { error: serializeError(error) };

        // DataFast is injected only on production
        if (window.datafast && emails.length > 0) {
          for (const email of emails) {
            window.datafast("resource_shared", {
              resource_id: `${resourceId}`,
              shared_with: `${email}`,
              role: `${access}`,
            });
          }
        }

        return { data };
      },

      // We need to invalidate all resources and permissions to ensure that
      // inhereied permissions are properly updated and reflected in the UI
      invalidatesTags: [{ type: "Resources" }, { type: "ResourcePermissions" }],
    }),

    unshareResource: build.mutation<
      Database["public"]["Functions"]["unshare_resource"]["Returns"],
      { resourceId: string; unshareWith: string }
    >({
      queryFn: async ({ resourceId, unshareWith }) => {
        const { data, error } = await supabase.rpc("unshare_resource", {
          p_resource_id: resourceId,
          p_shared_with: unshareWith,
        });

        if (error) return { error: serializeError(error) };

        return { data };
      },

      // We need to invalidate all resources and permissions to ensure that
      // inhereied permissions are properly updated and reflected in the UI
      invalidatesTags: [{ type: "Resources" }, { type: "ResourcePermissions" }],
    }),

    createFolder: build.mutation<
      string,
      {
        folder: NewFolder;
      }
    >({
      queryFn: async ({ folder }) => {
        const { data: newFolderId, error } = await supabase.rpc("create_folder", {
          p_name: folder.name,
          p_parent_id: folder.parent_id || undefined,
          p_icon: folder.icon || undefined,
        });

        if (error) return { error: serializeError(error) };

        return { data: newFolderId };
      },
      invalidatesTags: [
        {
          type: "Resources",
        },
      ],
    }),
  }),
});

const {
  useGetResourcesQuery,
  useGetResourceQuery,
  useDeleteResourceMutation,
  useCreateFolderMutation,
  useMoveResourceMutation,
  useRenameResourceMutation,
  useRestoreResourcesMutation,
  useGetResourcePermissionsQuery,
  useShareResourceMutation,
  useUnshareResourceMutation,
  useGetDeletedResourcesQuery,
  useHardDeleteResourceMutation,
} = resourcesApi;

export {
  resourcesApi,
  useGetResourcesQuery,
  useGetResourceQuery,
  useDeleteResourceMutation,
  useCreateFolderMutation,
  useMoveResourceMutation,
  useRenameResourceMutation,
  useRestoreResourcesMutation,
  useGetResourcePermissionsQuery,
  useShareResourceMutation,
  useUnshareResourceMutation,
  useGetDeletedResourcesQuery,
  useHardDeleteResourceMutation,
};
